/**
 * Клиент договоров (SK-6 — реестр; E5 — карточка). Типы согласованы с сервером по значениям, не
 * импортом (клиент и api типы напрямую не делят) — формы из apps/api/src/contracts/store.ts. Путь
 * относительный (`/api/...`): в проде проксирует Caddy, локально — dev-прокси Vite; сессия — cookie
 * HttpOnly (Р-5), токенов в JS нет.
 */

import type { Role } from "./roles";
import type { CounterpartyContact } from "./counterparties";

/** Направление договора (значения = enum `ContractDirection` сервера). */
export type ContractDirection = "sale" | "purchase";

/** Статус договора (значения = enum `ContractStatus` сервера). */
export type ContractStatus = "active" | "archived";

/**
 * Инициатор договора (E17) — работник, которому договор поручен.
 *
 * `isActive` приходит вместе с именем намеренно: деактивированная учётка не исчезает из карточки,
 * иначе договор выглядел бы бесхозным, хотя ответственный у него был — и был назначен осознанно.
 */
export interface ContractInitiator {
  readonly id: string;
  readonly fullName: string;
  readonly isActive: boolean;
}

/** Строка реестра договоров — ответ GET /api/contracts. */
export interface Contract {
  readonly id: string;
  readonly number: string;
  /** Календарная дата `ГГГГ-ММ-ДД` (правило 3); в UI показывается как `ДД.ММ.ГГГГ`. */
  readonly date: string;
  readonly direction: ContractDirection;
  readonly subject: string | null;
  readonly status: ContractStatus;
  /** Номер по внутреннему Реестру договоров заказчика (E17); нумерация ведётся вне Системы. */
  readonly registryNumber: string | null;
  /** `null` — «Инициатор не назначен»: так живут договоры, заведённые до E17, и ретро-ввод. */
  readonly initiator: ContractInitiator | null;
  readonly counterparty: {
    readonly name: string;
    readonly inn: string | null;
  };
}

/**
 * Отбор реестра (E17). Пустой запрос = весь реестр — тем же методом питается выпадающий список
 * договоров в реестре обязательств, и молча обрезать его нельзя.
 */
export interface ContractsQuery {
  /** «Мои» — договоры обращающегося; «не назначен» — договоры без Инициатора. */
  readonly initiator?: "mine" | "unassigned";
  /** Поиск по номеру договора, регистрационному номеру Реестра и наименованию контрагента. */
  readonly q?: string;
}

/** Наименования направлений (термины ТЗ 4.2.1 / прототип). */
export const DIRECTION_LABELS: Record<ContractDirection, string> = {
  sale: "Мы — поставщик",
  purchase: "Мы — покупатель",
};

/** Наименования статусов договора (В11: действует / архив). */
export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  active: "Действует",
  archived: "Архив",
};

/** Результат загрузки реестра: успех со списком либо типизированная причина отказа. */
export type ContractsResult =
  | { readonly ok: true; readonly contracts: Contract[] }
  | { readonly ok: false; readonly reason: "unauthorized" | "network"; readonly message: string };

interface ContractsBody {
  contracts: Contract[];
}

/**
 * Загрузка реестра (GET /api/contracts). 401 отделяем от сетевой ошибки: при 401 сессия
 * истекла — контекст уведёт на вход; при недоступности api страница покажет ошибку с
 * возможностью повторить, а не «падает».
 */
export async function fetchContracts(query: ContractsQuery = {}): Promise<ContractsResult> {
  // ВЫЗОВ БЕЗ АРГУМЕНТОВ ОБЯЗАН ОТДАВАТЬ ВСЁ, как до E17: этим же методом наполняется выпадающий
  // список договоров в реестре обязательств, и подмешать туда чужой отбор значило бы молча
  // спрятать часть договоров от человека, который о существовании отбора не знает.
  const search = new URLSearchParams();
  if (query.initiator !== undefined) search.set("initiator", query.initiator);
  if (query.q !== undefined && query.q.trim() !== "") search.set("q", query.q.trim());
  const suffix = search.size === 0 ? "" : `?${search.toString()}`;

  let res: Response;
  try {
    res = await fetch(`/api/contracts${suffix}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }

  if (res.status === 401) {
    return { ok: false, reason: "unauthorized", message: "Сессия истекла. Войдите заново." };
  }
  if (!res.ok) {
    return { ok: false, reason: "network", message: "Не удалось загрузить реестр договоров." };
  }

  let body: ContractsBody | null;
  try {
    body = (await res.json()) as ContractsBody;
  } catch {
    return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  }
  return { ok: true, contracts: body?.contracts ?? [] };
}

// ── Карточка договора (E5) ────────────────────────────────────────────────────

/** Вид дочернего объекта договора (значения = enum `SpecificationKind` сервера). */
export type SpecificationKind = "specification" | "annex" | "agreement";

/** Русские наименования видов (термины ТЗ 4.2.1). */
export const SPEC_KIND_LABELS: Record<SpecificationKind, string> = {
  specification: "Спецификация",
  annex: "Приложение",
  agreement: "Доп. соглашение",
};

/** Спецификация/приложение/ДС в карточке (форма `SpecificationView` сервера). */
export interface Specification {
  readonly id: string;
  readonly contractId: string;
  readonly kind: SpecificationKind;
  readonly number: string;
  /** Дата `ГГГГ-ММ-ДД`; в UI — `ДД.ММ.ГГГГ`. */
  readonly date: string;
  /** Сумма — деньги строкой «1840000.00» (или null); в UI — `formatMoney`. */
  readonly amount: string | null;
  readonly subject: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Контрагент в карточке договора (реквизиты + контакты + аффилированность, ТЗ 4.2.1). */
export interface ContractCounterparty {
  readonly id: string;
  readonly name: string;
  readonly inn: string | null;
  readonly kpp: string | null;
  readonly ogrn: string | null;
  readonly address: string | null;
  readonly contacts: CounterpartyContact[];
  readonly affiliated: boolean;
}

/** От какой суммы берётся потолок неустойки, заданный процентом (CR-2а). */
export type ContractCapBase = "contract" | "spec";

/** Полная карточка договора — ответ GET /api/contracts/:id (форма `ContractDetail` сервера). */
export interface ContractDetail {
  readonly id: string;
  readonly number: string;
  readonly date: string;
  readonly direction: ContractDirection;
  readonly subject: string | null;
  readonly validityTerm: string | null;
  /** Ставки неустойки, % в день, строкой (или null); в UI — `formatRatePerDay`. */
  readonly penPayRate: string | null;
  readonly penDelRate: string | null;
  /** Договорный потолок неустойки — деньги строкой (или null). */
  readonly penCap: string | null;
  /** Тот же потолок, заданный ПРОЦЕНТОМ от суммы (CR-2а), и от какой суммы он берётся. */
  readonly penCapPercent: string | null;
  readonly penCapBase: ContractCapBase | null;
  /** Цена (сумма) договора — деньги строкой; у рамочных договоров её может не быть. */
  readonly price: string | null;
  readonly claimRequired: boolean;
  readonly claimDays: number | null;
  readonly court: string | null;
  readonly status: ContractStatus;
  /** Номер по внутреннему Реестру договоров заказчика (E17, ответ В40). */
  readonly registryNumber: string | null;
  /** Инициатор договора (E17, ответ В36); `null` — «не назначен». */
  readonly initiator: ContractInitiator | null;
  readonly counterparty: ContractCounterparty;
  readonly specifications: Specification[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Поля формы договора. Пустая строка в необязательном поле = «стереть» (сервер → null). */
export interface ContractInput {
  readonly counterpartyId?: string;
  readonly number?: string;
  readonly date?: string;
  readonly direction?: ContractDirection;
  readonly subject?: string;
  readonly validityTerm?: string;
  readonly penPayRate?: string;
  readonly penDelRate?: string;
  readonly penCap?: string;
  readonly penCapPercent?: string;
  /** Пустая строка = «стереть» — как и у остальных необязательных полей. */
  readonly penCapBase?: ContractCapBase | "";
  readonly price?: string;
  readonly claimRequired?: boolean;
  readonly claimDays?: number | null;
  readonly court?: string;
  /** Регистрационный номер по Реестру договоров (E17). Пустая строка = «стереть». */
  readonly registryNumber?: string;
  /**
   * Переназначение Инициатора (E17, ответ В36) — только юрист и администратор. Пустая строка =
   * «снять назначение». Отказ по праву даёт СЕРВЕР: отсутствие поля на экране — не запрет.
   */
  readonly initiatorId?: string | null;
}

/** Поля формы спецификации/ДС. */
export interface SpecificationInput {
  readonly kind?: SpecificationKind;
  readonly number?: string;
  readonly date?: string;
  readonly amount?: string;
  readonly subject?: string;
}

// ── Права (UI-подсказки; авторитетную проверку делает сервер, матрица 4.3, правило 5) ──

/** Роли, ведущие ПРАВКУ реквизитов договора (право `contract:register`). */
export const CONTRACT_REGISTER_ROLES: readonly Role[] = ["clerk", "lawyer", "admin"];
/**
 * Роли, которым доступно ЗАВЕДЕНИЕ нового договора (право `contract:create`) — все шесть
 * (CR-3 от 06.08.2026). Правка правовых параметров при этом осталась у прежних трёх:
 * от ставок, потолка и досудебного порядка считаются деньги.
 */
export const CONTRACT_CREATE_ROLES: readonly Role[] = [
  "clerk",
  "commercial",
  "accounting",
  "lawyer",
  "director",
  "admin",
];
/** Роли архивирования/возврата (право `contract:archive`; контроль-влияющая операция, В11). */
export const CONTRACT_ARCHIVE_ROLES: readonly Role[] = ["lawyer", "admin"];
/**
 * Роли, назначающие и меняющие Инициатора договора (право `contract:assign_initiator`, ответ
 * заказчика В36 от 16.09.2026). Смена Инициатора перекладывает ответственность за контроль
 * исполнения вместе с потоком личных предупреждений — операция контролирующей роли.
 */
export const INITIATOR_ASSIGN_ROLES: readonly Role[] = ["lawyer", "admin"];
/** Роли ведения спецификаций/ДС (право `obligation:manage`, ТЗ 4.3). */
export const SPEC_MANAGE_ROLES: readonly Role[] = ["lawyer", "admin"];

export function canRegisterContracts(roles: readonly Role[]): boolean {
  return roles.some((r) => CONTRACT_REGISTER_ROLES.includes(r));
}
/** Может ли роль ЗАВЕСТИ договор (CR-3). Авторитетная проверка — на сервере, правило 5. */
export function canCreateContracts(roles: readonly Role[]): boolean {
  return roles.some((r) => CONTRACT_CREATE_ROLES.includes(r));
}
export function canArchiveContracts(roles: readonly Role[]): boolean {
  return roles.some((r) => CONTRACT_ARCHIVE_ROLES.includes(r));
}
export function canManageSpecs(roles: readonly Role[]): boolean {
  return roles.some((r) => SPEC_MANAGE_ROLES.includes(r));
}
/** Может ли роль НАЗНАЧИТЬ или СМЕНИТЬ Инициатора договора (E17). Решает сервер, правило 5. */
export function canAssignInitiator(roles: readonly Role[]): boolean {
  return roles.some((r) => INITIATOR_ASSIGN_ROLES.includes(r));
}

/** Успех со значением либо типизированная причина отказа с готовым сообщением для UI. */
export type ApiResult<T> =
  | { readonly ok: true; readonly value: T }
  | {
      readonly ok: false;
      readonly reason: "unauthorized" | "forbidden" | "conflict" | "validation" | "network";
      readonly message: string;
    };

const JSON_HEADERS = { "Content-Type": "application/json", Accept: "application/json" } as const;

async function toFailure(res: Response): Promise<Extract<ApiResult<never>, { ok: false }>> {
  if (res.status === 401) {
    return { ok: false, reason: "unauthorized", message: "Сессия истекла. Войдите заново." };
  }
  const body = (await res.json().catch(() => null)) as { message?: string } | null;
  const message = body?.message;
  if (res.status === 403)
    return { ok: false, reason: "forbidden", message: message ?? "Недостаточно прав." };
  if (res.status === 409)
    return { ok: false, reason: "conflict", message: message ?? "Конфликт данных." };
  if (res.status === 404) {
    return { ok: false, reason: "network", message: message ?? "Договор не найден." };
  }
  if (res.status === 400) {
    return { ok: false, reason: "validation", message: message ?? "Проверьте введённые данные." };
  }
  return { ok: false, reason: "network", message: message ?? "Не удалось выполнить операцию." };
}

/** Карточка договора (GET /api/contracts/:id). */
export async function fetchContract(id: string): Promise<ApiResult<ContractDetail>> {
  let res: Response;
  try {
    res = await fetch(`/api/contracts/${encodeURIComponent(id)}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { contract?: ContractDetail } | null;
  if (!body?.contract)
    return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  return { ok: true, value: body.contract };
}

/**
 * Кого можно назначить Инициатором (GET /api/contracts/initiators, E17) — активные учётки,
 * только имя и идентификатор. Маршрут закрыт тем же правом, что и само назначение, поэтому
 * зовётся лишь с экрана, где есть выпадающий список.
 */
export async function fetchInitiatorCandidates(): Promise<ApiResult<ContractInitiator[]>> {
  let res: Response;
  try {
    res = await fetch("/api/contracts/initiators", {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { initiators?: ContractInitiator[] } | null;
  if (!body?.initiators)
    return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  return { ok: true, value: body.initiators };
}

async function sendContract(
  url: string,
  method: "POST" | "PATCH",
  input: ContractInput | { status: ContractStatus },
): Promise<ApiResult<ContractDetail>> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: JSON_HEADERS,
      credentials: "same-origin",
      body: JSON.stringify(input),
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { contract?: ContractDetail } | null;
  if (!body?.contract)
    return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  return { ok: true, value: body.contract };
}

/** Регистрация договора (POST /api/contracts). */
export function createContract(input: ContractInput): Promise<ApiResult<ContractDetail>> {
  return sendContract("/api/contracts", "POST", input);
}

/** Правка договора (PATCH /api/contracts/:id). */
export function updateContract(
  id: string,
  input: ContractInput,
): Promise<ApiResult<ContractDetail>> {
  return sendContract(`/api/contracts/${encodeURIComponent(id)}`, "PATCH", input);
}

/** Смена статуса — архив/возврат (PATCH /api/contracts/:id/status). */
export function setContractStatus(
  id: string,
  status: ContractStatus,
): Promise<ApiResult<ContractDetail>> {
  return sendContract(`/api/contracts/${encodeURIComponent(id)}/status`, "PATCH", { status });
}

async function sendSpec(
  url: string,
  method: "POST" | "PATCH",
  input: SpecificationInput,
): Promise<ApiResult<Specification>> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: JSON_HEADERS,
      credentials: "same-origin",
      body: JSON.stringify(input),
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { specification?: Specification } | null;
  if (!body?.specification) {
    return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  }
  return { ok: true, value: body.specification };
}

/** Добавить спецификацию/ДС (POST /api/contracts/:id/specifications). */
export function addSpecification(
  contractId: string,
  input: SpecificationInput,
): Promise<ApiResult<Specification>> {
  return sendSpec(`/api/contracts/${encodeURIComponent(contractId)}/specifications`, "POST", input);
}

/** Править спецификацию (PATCH /api/contracts/specifications/:specId). */
export function updateSpecification(
  specId: string,
  input: SpecificationInput,
): Promise<ApiResult<Specification>> {
  return sendSpec(`/api/contracts/specifications/${encodeURIComponent(specId)}`, "PATCH", input);
}

/** Удалить спецификацию (DELETE /api/contracts/specifications/:specId). */
export async function removeSpecification(specId: string): Promise<ApiResult<null>> {
  let res: Response;
  try {
    res = await fetch(`/api/contracts/specifications/${encodeURIComponent(specId)}`, {
      method: "DELETE",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (res.status === 204) return { ok: true, value: null };
  return toFailure(res);
}
